import { useState, useEffect, useMemo, useRef } from 'react'
import { generateAssetData } from '../../utils/generateAssetData'
import { Asset, MaintenanceRecord, AssetStatus } from '@/types'
import { format } from 'date-fns'
import { ChevronUpIcon, ChevronDownIcon, ArrowsUpDownIcon } from '@heroicons/react/20/solid'

type SortField = 'status' | 'lastMaintenanceDate' | 'operatingHours'
type SortDirection = 'asc' | 'desc' | false

interface DashboardData {
  assets: Asset[]
  maintenanceHistory: MaintenanceRecord[]
}

const statusColors: Record<AssetStatus, { selected: string; unselected: string }> = {
  operational: {
    selected: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    unselected: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
  },
  maintenance: {
    selected: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    unselected: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
  },
  repair: {
    selected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    unselected: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
  },
  standby: {
    selected: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    unselected: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
  },
  decommissioned: {
    selected: 'bg-gray-200 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
    unselected: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
  }
}

export default function AssetManagementDashboard() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null)
  const [selectedLocation, setSelectedLocation] = useState<string>('')
  const [selectedStatuses, setSelectedStatuses] = useState<AssetStatus[]>([])
  const [sortField, setSortField] = useState<SortField | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(false)
  const assetDetailsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const generatedData = generateAssetData()
    setData(generatedData)
  }, [])

  // Get unique locations from assets
  const locations = useMemo(() => {
    if (!data) return []
    const uniqueSites = new Set(data.assets.map(asset => asset.location.site))
    return Array.from(uniqueSites)
  }, [data])

  // Get unique statuses and their counts
  const statusCounts = useMemo(() => {
    if (!data) return new Map<AssetStatus, number>()
    return data.assets.reduce((acc, asset) => {
      acc.set(asset.status, (acc.get(asset.status) || 0) + 1)
      return acc
    }, new Map<AssetStatus, number>())
  }, [data])

  // Sort and filter assets
  const filteredAssets = useMemo(() => {
    if (!data) return []
    
    let filtered = data.assets.filter(asset => {
      const statusMatch = selectedStatuses.length === 0 || selectedStatuses.includes(asset.status)
      const locationMatch = !selectedLocation || asset.location.site === selectedLocation
      return statusMatch && locationMatch
    })

    if (sortField && sortDirection) {
      filtered.sort((a, b) => {
        let comparison = 0
        
        switch (sortField) {
          case 'status':
            comparison = a.status.localeCompare(b.status)
            break
          case 'lastMaintenanceDate':
            comparison = a.lastMaintenanceDate.getTime() - b.lastMaintenanceDate.getTime()
            break
          case 'operatingHours':
            comparison = a.operatingHours - b.operatingHours
            break
        }
        
        return sortDirection === 'asc' ? comparison : -comparison
      })
    }

    return filtered
  }, [data, selectedStatuses, selectedLocation, sortField, sortDirection])

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') setSortDirection('desc')
      else if (sortDirection === 'desc') {
        setSortDirection(false)
        setSortField(null)
      }
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const handleAssetSelect = (asset: Asset) => {
    setSelectedAsset(asset)
    // Add a small delay to ensure the DOM has updated
    setTimeout(() => {
      assetDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }

  if (!data) {
    return <div className="flex items-center justify-center h-full">Loading...</div>
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Selected Asset Section */}
      <div ref={assetDetailsRef} className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 sm:p-6">
        {selectedAsset ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Asset Details */}
            <div>
              <h2 className="text-lg font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">
                Asset Details
              </h2>
              <div className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Model</p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {selectedAsset.modelNumber}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Serial Number
                    </p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {selectedAsset.serialNumber}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Manufacturer
                    </p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {selectedAsset.manufacturer}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Installation Date
                    </p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {format(selectedAsset.installationDate, 'MMM d, yyyy')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Location</p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {`${selectedAsset.location.site} - ${selectedAsset.location.area}`}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {selectedAsset.status.charAt(0).toUpperCase() + selectedAsset.status.slice(1)}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Specifications
                  </p>
                  <div className="mt-1 text-sm text-gray-900 dark:text-white">
                    <p>Dimensions: {selectedAsset.specifications.dimensions}</p>
                    <p>Weight: {selectedAsset.specifications.weight} kg</p>
                    <p>Power: {selectedAsset.specifications.power}</p>
                    <p>Capacity: {selectedAsset.specifications.capacity}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Maintenance Statistics */}
            <div>
              <h2 className="text-lg font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">
                Maintenance Statistics
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                Maintenance statistics coming soon...
              </p>
            </div>
          </div>
        ) : (
          <div className="p-3 sm:p-6 text-center">
            <div className="text-gray-500 dark:text-gray-400">
              <svg className="mx-auto h-10 w-10 sm:h-12 sm:w-12 mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="text-lg font-medium mb-2">Placeholder</h3>
              <p>Select an asset from the table below to view its details</p>
            </div>
          </div>
        )}
      </div>

      {/* Equipment Overview Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Equipment Overview
              </h2>
              <div className="flex items-center gap-2 sm:gap-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {filteredAssets.length} assets
                </span>
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="px-2 sm:px-3 py-1.5 sm:py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm"
                >
                  <option value="">All Locations</option>
                  {locations.map(location => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {Array.from(statusCounts.entries()).map(([status, count]) => {
                const isSelected = selectedStatuses.includes(status)
                const colorClass = isSelected ? statusColors[status].selected : statusColors[status].unselected
                
                return (
                  <button
                    key={status}
                    onClick={() => {
                      setSelectedStatuses(prev => {
                        const newStatuses = isSelected
                          ? prev.filter(s => s !== status)
                          : [...prev, status]
                        return newStatuses
                      })
                    }}
                    className={`inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm ${colorClass}`}
                  >
                    <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
                    <span className="text-xs">({count})</span>
                    {isSelected && (
                      <span className="font-medium">&times;</span>
                    )}
                  </button>
                )
              })}
              {selectedStatuses.length >= 2 && (
                <button
                  onClick={() => setSelectedStatuses([])}
                  className="text-xs sm:text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                >
                  deselect all
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Serial Number
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button
                    onClick={() => toggleSort('status')}
                    className="flex items-center gap-2 hover:text-gray-700 dark:hover:text-gray-200"
                  >
                    Status
                    {sortField === 'status' ? (
                      sortDirection === 'asc' ? (
                        <ChevronUpIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      )
                    ) : (
                      <ArrowsUpDownIcon className="w-4 h-4 text-gray-400 dark:text-gray-600" />
                    )}
                  </button>
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button
                    onClick={() => toggleSort('lastMaintenanceDate')}
                    className="flex items-center gap-2 hover:text-gray-700 dark:hover:text-gray-200"
                  >
                    Last Maintenance
                    {sortField === 'lastMaintenanceDate' ? (
                      sortDirection === 'asc' ? (
                        <ChevronUpIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      )
                    ) : (
                      <ArrowsUpDownIcon className="w-4 h-4 text-gray-400 dark:text-gray-600" />
                    )}
                  </button>
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button
                    onClick={() => toggleSort('operatingHours')}
                    className="flex items-center gap-2 hover:text-gray-700 dark:hover:text-gray-200"
                  >
                    Operating Hours
                    {sortField === 'operatingHours' ? (
                      sortDirection === 'asc' ? (
                        <ChevronUpIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      )
                    ) : (
                      <ArrowsUpDownIcon className="w-4 h-4 text-gray-400 dark:text-gray-600" />
                    )}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredAssets.map((asset) => (
                <tr
                  key={asset.id}
                  onClick={() => handleAssetSelect(asset)}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer ${
                    selectedAsset?.id === asset.id ? 'bg-gray-50 dark:bg-gray-700' : ''
                  }`}
                >
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-white">
                    {asset.modelNumber}
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    {asset.serialNumber}
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    {`${asset.location.site} - ${asset.location.area}`}
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${statusColors[asset.status].selected}`}>
                      {asset.status.charAt(0).toUpperCase() + asset.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    {format(asset.lastMaintenanceDate, 'MMM d, yyyy')}
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    {asset.operatingHours.toLocaleString()} hrs
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
} 